/**
 * Draft saving queries.
 *
 * Drafts are user-scoped JSON snapshots saved by the API foundation added in
 * Patch 013. Screens decide what shape their payload uses; this file only
 * provides typed transport helpers and cache invalidation.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest, type ApiError } from './api-client';

export type DraftType = 'PROJECT_CREATE' | 'GENERAL_PARTICULARS' | 'SCHEDULE_OF_STORES' | 'CSV_IMPORT';

export interface DraftRecord {
  id: string;
  draftType: DraftType;
  projectId: string | null;
  entityId: string | null;
  payloadJson: Record<string, unknown>;
  createdByUserId: string;
  updatedByUserId: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  deletedByUserId: string | null;
  rowVersion: number;
}

interface PaginatedList<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

export interface DraftListFilters {
  draftType?: DraftType;
  projectId?: string | null;
  entityId?: string | null;
}

export interface SaveDraftInput {
  draftType: DraftType;
  projectId?: string | null;
  entityId?: string | null;
  payloadJson: Record<string, unknown>;
}

export const draftsKey = (filters: DraftListFilters = {}) => ['drafts', filters] as const;

export function useDraftsList(filters: DraftListFilters = {}, enabled: boolean = true) {
  return useQuery({
    queryKey: draftsKey(filters),
    queryFn: ({ signal }) =>
      apiRequest<PaginatedList<DraftRecord>>('/api/drafts', {
        signal,
        query: {
          draftType: filters.draftType,
          projectId: filters.projectId ?? undefined,
          entityId: filters.entityId ?? undefined,
          pageSize: 20,
        },
      }),
    enabled,
    staleTime: 30_000,
  });
}

export function useSaveDraft() {
  const qc = useQueryClient();
  return useMutation<DraftRecord, ApiError, SaveDraftInput>({
    mutationFn: (body) => apiRequest<DraftRecord>('/api/drafts', { method: 'POST', body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['drafts'] });
    },
  });
}

export function useDiscardDraft() {
  const qc = useQueryClient();
  return useMutation<{ deleted: boolean }, ApiError, { id: string; rowVersion: number }>({
    mutationFn: ({ id, rowVersion }) =>
      apiRequest<{ deleted: boolean }>(`/api/drafts/${id}`, {
        method: 'DELETE',
        query: { rowVersion },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['drafts'] });
    },
  });
}
